// frontend/src/components/TreasuryAccountSelector.jsx
import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Icon } from './ui';

/**
 * Composant sélecteur de compte trésorerie (caisse ou banque)
 * Affiche les comptes disponibles selon le mode de paiement
 */
export default function TreasuryAccountSelector({ 
  paymentMethod, 
  selectedAccountId, 
  onChange,
  disabled = false
}) {
  const { activeWorkspace } = useWorkspace(); // ✅ AJOUT
  const [cashRegisters, setCashRegisters] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Déterminer le type de compte selon le mode de paiement
  const accountType = () => {
    const cashMethods = ['cash', 'CASH'];
    const bankMethods = ['check', 'bank_transfer', 'BANK_TRANSFER', 'CHECK', 'card', 'CARD', 'online', 'draft'];
    
    if (cashMethods.includes(paymentMethod)) return 'cash';
    if (bankMethods.includes(paymentMethod)) return 'bank';
    return null; // Pour RS ou Autre, pas de compte trésorerie
  };

  const type = accountType();

  // ✅ Charger les comptes disponibles - AVEC DÉPENDANCE SUR activeWorkspace
  useEffect(() => {
    const loadAccounts = async () => {
      // ✅ Vérifier qu'un workspace est actif
      if (!activeWorkspace) {
        console.warn('⚠️ Aucun workspace actif - TreasuryAccountSelector');
        setCashRegisters([]);
        setBankAccounts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        console.log('🔄 Chargement comptes trésorerie pour workspace:', activeWorkspace._id);
        
        // Charger les deux types de comptes en parallèle
        const [cashRes, bankRes] = await Promise.all([
          api.get('/treasury/cash').catch(() => ({ data: { registers: [] } })),
          api.get('/treasury/bank').catch(() => ({ data: { accounts: [] } }))
        ]);
        
        console.log('✅ Caisses chargées:', cashRes.data.registers?.length || 0);
        console.log('✅ Comptes bancaires chargés:', bankRes.data.accounts?.length || 0);
        
        setCashRegisters(cashRes.data.registers || []);
        setBankAccounts(bankRes.data.accounts || []);
      } catch (error) {
        console.error('❌ Erreur chargement comptes trésorerie:', error);
        setCashRegisters([]);
        setBankAccounts([]);
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  }, [activeWorkspace]); // ✅ AJOUT DE LA DÉPENDANCE

  // Ne rien afficher si le mode ne nécessite pas de compte trésorerie
  if (!type) {
    return null;
  }

  // Comptes à afficher selon le type
  const accounts = type === 'cash' ? cashRegisters : bankAccounts;
  const activeAccounts = accounts.filter(acc => acc.status === 'active');

  // Sélectionner automatiquement le premier compte s'il n'y en a qu'un
  useEffect(() => {
    if (activeAccounts.length === 1 && !selectedAccountId) {
      onChange(activeAccounts[0]._id);
    }
  }, [activeAccounts, selectedAccountId, onChange]);

  if (loading) {
    return (
      <div className="text-sm text-gray-500 p-2">
        Chargement des comptes...
      </div>
    );
  }

  if (activeAccounts.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
        <div className="flex items-start gap-2">
          <Icon name="IconAlertTriangle" className="text-yellow-600 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">
              Aucun{type === 'cash' ? 'e caisse' : ' compte bancaire'} disponible
            </p>
            <p className="mt-1">
              Créez un{type === 'cash' ? 'e caisse' : ' compte bancaire'} dans la section Trésorerie 
              pour enregistrer automatiquement les encaissements.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-TN', {
      style: 'currency',
      currency: 'TND',
    }).format(amount);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">
        {type === 'cash' ? 'Caisse' : 'Compte bancaire'} *
      </label>
      <select
        className={`w-full border p-2 rounded ${
          type === 'cash' ? 'bg-green-50 border-green-300' : 'bg-blue-50 border-blue-300'
        }`}
        value={selectedAccountId || ''}
        onChange={(e) => onChange(e.target.value)}
        required
        disabled={disabled}
      >
        <option value="">
          Sélectionner {type === 'cash' ? 'une caisse' : 'un compte'}
        </option>
        {activeAccounts.map((account) => (
          <option key={account._id} value={account._id}>
            {account.name}
            {type === 'bank' && ` - ${account.bankName}`}
            {account.location && ` (${account.location})`}
            {' - Solde: '}
            {formatCurrency(account.currentBalance)}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        {type === 'cash' 
          ? 'Le montant sera ajouté au solde de cette caisse'
          : 'Le montant sera ajouté au solde de ce compte bancaire'
        }
      </p>
    </div>
  );
}